import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, Edit, Trash2, X } from 'lucide-react';
import { userService } from '../../_services';

function WholesalerOverride({
    text,
    whEmail,
    whId,
    whName,
    closeOverridePopup,
    openEditProfilePopup,
    whPrefix,
    whProfilePagin,
    whSearch
}) {
    const [loading, setLoading] = useState(true);
    const [btnLoader, setBtnLoader] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [userList, setUserList] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [showDropdown, setShowDropdown] = useState({});
    const [originalOverrides, setOriginalOverrides] = useState({}); // Store original values before editing
    const [hasInitialOverrides, setHasInitialOverrides] = useState(false); // Track if initial overrides existed

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                const data = text && JSON.parse(text);
                return data;
            } catch (error) {
                return [];
            }
        });
    }, []);

    const fetchOverrides = useCallback(async (whname, wholesalerId, mname) => {
        setLoading(true);
        setError('');
        
        try {
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ whname, wholesaler_Id: wholesalerId, mname })
            };

            const response = await fetch(`/api/admin/Wh_Overrides_Users`, requestOptions);

            const apidata = await handleResponse(response);

            if (apidata && apidata.length > 0) {
                if (apidata[0]["all_Overrides"]?.length > 0) {
                    setUserList(apidata[0]["all_Overrides"]);
                }

                if (apidata[0]["user_Overrides"]?.length > 0) {
                    setHasInitialOverrides(true); // Mark that initial overrides exist
                    const userOverrides = apidata[0]["user_Overrides"].map(obj => ({
                        ...obj,
                        id: Math.floor(10000 + Math.random() * 90000),
                        editable: obj.whName === '' || obj.whName === null || obj.whName === undefined,
                        isNew: false // Mark as existing (from backend)
                    }));
                    setOverrides(userOverrides);
                } else {
                    setHasInitialOverrides(false); // No initial overrides
                    // Add initial empty row
                    setOverrides([{
                        whId: "",
                        whName: "",
                        commission: "",
                        emailId: "",
                        editable: true,
                        isNew: true, // Mark as new row
                        id: Math.floor(10000 + Math.random() * 90000)
                    }]);
                }
            } else {
                setHasInitialOverrides(false); // No initial overrides
                // Add initial empty row if no data
                setOverrides([{
                    whId: "",
                    whName: "",
                    commission: "",
                    emailId: "",
                    editable: true,
                    isNew: true, // Mark as new row
                    id: Math.floor(10000 + Math.random() * 90000)
                }]);
            }
        } catch (error) {

            setError('Oops!, Something went wrong. Try Again.');
        } finally {

            setLoading(false);
        }
    }, [handleResponse]);

    useEffect(() => {

        if (whId && whName) {

            fetchOverrides(whEmail || '', parseInt(whId), whName);
        } else {

            setLoading(false);
        }
    }, [whEmail, whId, whName, fetchOverrides]);

    const addOverrideRow = useCallback(() => {
        const newRow = {
            whId: "",
            whName: "",
            commission: "",
            emailId: "",
            editable: true,
            isNew: true, // Mark as new row
            id: Math.floor(10000 + Math.random() * 90000)
        };
        setOverrides(prev => [...prev, newRow]);
    }, []);

    const handleOverrideChange = useCallback((index, field, value) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
        
        // Reopen dropdown when user types in the wholesaler name field
        if (field === 'whName') {
            setShowDropdown(prev => ({ ...prev, [index]: true }));
        }
    }, []);

    const handleSelectWholesaler = useCallback((index, wholesaler) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                whId: wholesaler.whId,
                whName: wholesaler.whName,
                emailId: wholesaler.emailId,
                commission: wholesaler.commission
            };
            return updated;
        });
        setShowDropdown(prev => ({ ...prev, [index]: false }));
    }, []);

    const handleClearWholesaler = useCallback((index) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                whId: "",
                whName: "",
                emailId: "",
                commission: ""
            };
            return updated;
        });
        // Reopen dropdown after clearing so user can search immediately
        setShowDropdown(prev => ({ ...prev, [index]: true }));
    }, []);

    const handleInputFocus = useCallback((index) => {
        setShowDropdown(prev => ({ ...prev, [index]: true }));
    }, []);

    const handleInputBlur = useCallback((index) => {
        // Delay to allow click on dropdown item
        setTimeout(() => {
            setShowDropdown(prev => ({ ...prev, [index]: false }));
        }, 200);
    }, []);

    const handleEdit = useCallback((index) => {
        // Store original values before editing
        const override = overrides[index];
        setOriginalOverrides(prev => ({
            ...prev,
            [index]: { ...override }
        }));
        
        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], editable: true };
            return updated;
        });
    }, [overrides]);

    const saveData = useCallback((index) => {
        const override = overrides[index];
        
        if (!override.whName || !override.emailId || !override.commission) {
            setError("Fields can't be blank and commission should be a numeric value e.g. 0.10");
            return;
        }

        if (!userService.validateCurrency(override.commission)) {
            setError("Commission should be a numeric value e.g. 0.10");
            return;
        }

        if (parseFloat(override.commission) > 10) {
            setError('Commission should not be more than 10');
            return;
        }

        if (parseFloat(override.commission) <= 0) {
            setError('Commission should be more than 0');
            return;
        }

        setOverrides(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], editable: false, isNew: false }; // Mark as saved/existing
            return updated;
        });
        setError('');
    }, [overrides]);

    const handleCancel = useCallback((index) => {
        const override = overrides[index];
        
        // Check if this is a new row that was never saved
        if (override.isNew) {
            // Remove new rows completely
            setOverrides(prev => {
                const updated = [...prev];
                updated.splice(index, 1);
                return updated;
            });
        } else if (originalOverrides[index]) {
            // Restore original values for edited existing rows
            setOverrides(prev => {
                const updated = [...prev];
                updated[index] = { ...originalOverrides[index], editable: false };
                return updated;
            });
            // Clear stored original data
            setOriginalOverrides(prev => {
                const updated = { ...prev };
                delete updated[index];
                return updated;
            });
        } else {
            // Just set to non-editable if it's an existing row but wasn't edited
            setOverrides(prev => {
                const updated = [...prev];
                updated[index] = { ...updated[index], editable: false };
                return updated;
            });
        }
        setError('');
    }, [overrides, originalOverrides]);

    const deleteRow = useCallback((index) => {
        setOverrides(prev => {
            const updated = [...prev];
            updated.splice(index, 1);
            return updated;
        });
    }, []);

    const handleSaveSubmit = useCallback(async (e) => {
        e.preventDefault();
        setBtnLoader(true);
        setError('');
        setSuccessMessage('');

        const isEditMode = overrides.some(row => row.editable === true);
        if (isEditMode) {
            setError('Please enter overrides details in row else can remove row');
            setBtnLoader(false);
            return;
        }

        const overrideJson = [];
        let errorSet = 0;

        for (let i = 0; i < overrides.length; i++) {
            const override = overrides[i];
            if (!override.whId || !override.commission) {
                errorSet += 1;
                setError('Please enter overrides details in row else can remove row');
                break;
            }
            overrideJson.push({
                Wh_Parent_Id: whId,
                Wh_Override_Id: override.whId,
                Wh_Commission: override.commission
            });
        }

        if (errorSet > 0) {
            setBtnLoader(false);
            return;
        }

        try {
            const overridesHeader = JSON.stringify(overrideJson);
            const action = hasInitialOverrides ? 'edit overrides' : 'save overrides'; // String values from production-v3
            const requestOptions = {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'whOverrides': overridesHeader
                },
                body: JSON.stringify({
                    roleName: userdata.username,
                    roleType: userdata.userrole,
                    id: userdata.id,
                    email: userdata.email,
                    action: action, // 'save overrides' or 'edit overrides'
                    wholesaler_id: parseInt(whId, 10) 
                })
            };

            const response = await fetch(`/api/admin/Process_Wh_Override`, requestOptions);
            const apidata = await handleResponse(response);

            if (apidata?.length > 0) {
                if (apidata[0].status_code === "1") {
                    setSuccessMessage(apidata[0].status_message);
                    setTimeout(() => {
                        handleClose();
                    }, 2000);
                } else {
                    setError(apidata[0].status_message || 'Error saving overrides');
                }
            } else {
                setError('Oops!, Something went wrong. Try Again.');
            }
        } catch (error) {

            setError('Oops!, Something went wrong. Try Again.');
        } finally {
            setBtnLoader(false);
        }
    }, [overrides, whId, userdata, handleResponse, hasInitialOverrides]);

    const handleClose = useCallback(() => {
        closeOverridePopup(whName, whId, whPrefix, whProfilePagin, whSearch);
        openEditProfilePopup(whName, whId, whPrefix, whProfilePagin, whSearch);
    }, [closeOverridePopup, openEditProfilePopup, whName, whId, whPrefix, whProfilePagin, whSearch]);

    return (
        <Dialog open={true} onOpenChange={handleClose}>
            <DialogContent className="max-w-5xl h-[calc(100vh-3rem)]">
                <div className="flex flex-col h-full">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Override: {whName}</DialogTitle>
                    </DialogHeader>

                    {loading ? (
                        <div className="flex-1 flex justify-center items-center">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                    ) : (
                        <form onSubmit={handleSaveSubmit} className="flex flex-col flex-1 min-h-0 space-y-4">
                            {/* Fixed Header with Add Button */}
                            <div className="flex-shrink-0 flex justify-end items-center">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={addOverrideRow}
                                    disabled={btnLoader || loading}
                                >
                                    Add New Override
                                </Button>
                            </div>

                            {/* Scrollable Table */}
                            {overrides.length > 0 ? (
                                <div className="flex-1 overflow-y-auto min-h-0 border rounded-lg">
                                    <Table wrapperClassName="relative w-full overflow-visible">
                                    <TableHeader className="sticky top-0 z-10 bg-background border-b">
                                        <TableRow>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Email</TableHead>
                                            <TableHead>Commission</TableHead>
                                            <TableHead className="w-[150px]">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {overrides.map((override, index) => (
                                            <TableRow key={override.id}>
                                                <TableCell className="relative">
                                                    {override.editable ? (
                                                        <div className="relative w-full">
                                                            <div className="relative">
                                                                <Input
                                                                    type="text"
                                                                    value={override.whName}
                                                                    onChange={(e) => handleOverrideChange(index, 'whName', e.target.value)}
                                                                    onFocus={() => handleInputFocus(index)}
                                                                    onBlur={() => handleInputBlur(index)}
                                                                    placeholder="Search wholesaler..."
                                                                    className="w-full pr-8"
                                                                />
                                                                {override.whName && (
                                                                    <Button
                                                                        type="button"
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="absolute right-0 top-0 h-full px-2 hover:bg-transparent"
                                                                        onClick={() => handleClearWholesaler(index)}
                                                                    >
                                                                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                                                                    </Button>
                                                                )}
                                                            </div>
                                                            {showDropdown[index] && userList.filter(wh => 
                                                                wh.whName.toLowerCase().includes(override.whName.toLowerCase())
                                                            ).length > 0 && (
                                                                <div 
                                                                    className="absolute left-0 top-full z-[9999] w-full mt-1 bg-popover border rounded-md shadow-lg overflow-y-auto min-w-[200px] min-h-[120px] max-h-[250px]"
                                                                >
                                                                    {userList
                                                                        .filter(wh => wh.whName.toLowerCase().includes(override.whName.toLowerCase()))
                                                                        .map((wh, idx) => (
                                                                            <div
                                                                                key={idx}
                                                                                className="px-3 py-2 cursor-pointer hover:bg-accent text-sm"
                                                                                onMouseDown={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleSelectWholesaler(index, wh);
                                                                                }}
                                                                            >
                                                                                {wh.whName}
                                                                            </div>
                                                                        ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        override.whName
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {override.editable ? (
                                                        <Input
                                                            type="email"
                                                            value={override.emailId}
                                                            disabled
                                                            className="bg-muted"
                                                        />
                                                    ) : (
                                                        override.emailId
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {override.editable ? (
                                                        <Input
                                                            type="text"
                                                            value={override.commission}
                                                            onChange={(e) => handleOverrideChange(index, 'commission', e.target.value)}
                                                        />
                                                    ) : (
                                                        override.commission
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {override.editable ? (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                type="button"
                                                                onClick={() => saveData(index)}
                                                            >
                                                                Save
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="secondary"
                                                                onClick={() => handleCancel(index)}
                                                            >
                                                                Cancel
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div className="flex gap-2">
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => handleEdit(index)}
                                                            >
                                                                <Edit className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="ghost"
                                                                onClick={() => deleteRow(index)}
                                                            >
                                                                <Trash2 className="h-4 w-4 text-destructive" />
                                                            </Button>
                                                        </div>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-muted-foreground">
                                No overrides added yet. Click "Add New Override" to get started.
                            </div>
                        )}

                        {/* Fixed Messages */}
                        {successMessage && (
                            <div className="flex-shrink-0 text-sm text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 p-3 rounded-md">
                                {successMessage}
                            </div>
                        )}

                        {error && (
                            <div className="flex-shrink-0 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                                {error}
                            </div>
                        )}

                        {/* Fixed Footer */}
                        <DialogFooter className="flex-shrink-0">
                            <Button type="submit" disabled={btnLoader}>
                                {btnLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save & Submit
                            </Button>
                            <Button type="button" variant="secondary" onClick={handleClose} disabled={btnLoader}>
                                Cancel
                            </Button>
                        </DialogFooter>
                    </form>
                )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default WholesalerOverride;
